import { Alert, Button, Card, Form, Input, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { StaffLoginInput } from '@riba/shared';
import { endpoints } from '../api/endpoints';
import { ApiRequestError } from '../api/http';
import { useAuthStore } from './auth.store';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onFinish(values: StaffLoginInput): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const res = await endpoints.auth.staffLogin(values);
      setSession(res.accessToken, res.user);
      void navigate('/clients', { replace: true });
    } catch (err) {
      // Show the mapped message for known codes; fall back to the server text.
      const code = err instanceof ApiRequestError ? err.code : null;
      const key = `login.errors.${code}`;
      const translated = code ? t(key) : null;
      setError(
        translated && translated !== key
          ? translated
          : err instanceof Error
            ? err.message
            : t('common.error'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: '#f0f2f5',
        padding: 16,
      }}
    >
      <Card style={{ width: '100%', maxWidth: 380 }}>
        <Typography.Title level={4} style={{ marginTop: 0 }}>
          {t('login.title')}
        </Typography.Title>
        <Typography.Paragraph type="secondary">{t('login.subtitle')}</Typography.Paragraph>

        {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} showIcon />}

        <Form<StaffLoginInput> layout="vertical" onFinish={onFinish} autoComplete="on">
          <Form.Item name="login" label={t('login.login')} rules={[{ required: true }]}>
            <Input autoFocus autoComplete="username" />
          </Form.Item>
          <Form.Item name="password" label={t('login.password')} rules={[{ required: true }]}>
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting} block>
            {t('login.submit')}
          </Button>
        </Form>
      </Card>
    </div>
  );
}
